import {
  collection,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  query,
  orderBy,
  where,
  getCountFromServer,
  serverTimestamp,
  getFirestore,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, getStorage } from 'firebase/storage';
import app from '../config/firebaseConfig';

const db = getFirestore(app);
const storage = getStorage(app);

const COLLECTION = 'poojas';

// ─── Fetch paginated poojas ────────────────────────────────────────────────
export const fetchPoojas = async ({ pageSize = 10, search = '', activeOnly = null } = {}) => {
  try {
    let q = query(collection(db, COLLECTION), orderBy('createdAt', 'desc'));

    if (activeOnly !== null) {
      q = query(collection(db, COLLECTION), where('isActive', '==', activeOnly), orderBy('createdAt', 'desc'));
    }

    const snapshot = await getDocs(q);
    let poojas = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));

    if (search) {
      const lower = search.toLowerCase();
      poojas = poojas.filter(p =>
        p.poojaName?.toLowerCase().includes(lower) ||
        p.poojaId?.toLowerCase().includes(lower) ||
        p.description?.toLowerCase().includes(lower)
      );
    }

    return { success: true, data: poojas, total: poojas.length };
  } catch (error) {
    console.error('fetchPoojas error:', error);
    return { success: false, error: error.message, data: [] };
  }
};

// ─── Get single pooja ─────────────────────────────────────────────────────
export const getPoojaById = async (poojaDocId) => {
  try {
    const snap = await getDoc(doc(db, COLLECTION, poojaDocId));
    if (!snap.exists()) return { success: false, error: 'Pooja not found' };
    return { success: true, data: { id: snap.id, ...snap.data() } };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// ─── Create pooja ─────────────────────────────────────────────────────────
export const createPooja = async (formData, imageFile) => {
  try {
    let imageUrl = '';
    if (imageFile) {
      imageUrl = await uploadPoojaImage(imageFile);
    }

    const payload = {
      ...formData,
      imageUrl,
      isActive: formData.isActive ?? true,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    const docRef = await addDoc(collection(db, COLLECTION), payload);
    return { success: true, id: docRef.id };
  } catch (error) {
    console.error('createPooja error:', error);
    return { success: false, error: error.message };
  }
};

// ─── Update pooja ─────────────────────────────────────────────────────────
export const updatePooja = async (poojaDocId, formData, imageFile) => {
  try {
    let imageUrl = formData.imageUrl || '';
    if (imageFile) {
      imageUrl = await uploadPoojaImage(imageFile);
    }

    const payload = {
      ...formData,
      imageUrl,
      updatedAt: serverTimestamp(),
    };

    delete payload.createdAt; // preserve original
    delete payload.id;        // remove Firestore doc id

    // Strip any undefined values — Firestore rejects them
    Object.keys(payload).forEach(key => {
      if (payload[key] === undefined) delete payload[key];
    });

    await updateDoc(doc(db, COLLECTION, poojaDocId), payload);
    return { success: true };
  } catch (error) {
    console.error('updatePooja error:', error);
    return { success: false, error: error.message };
  }
};

// ─── Toggle active status ──────────────────────────────────────────────────
export const togglePoojaStatus = async (poojaDocId, isActive) => {
  try {
    await updateDoc(doc(db, COLLECTION, poojaDocId), {
      isActive,
      updatedAt: serverTimestamp(),
    });
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// ─── Delete pooja ─────────────────────────────────────────────────────────
export const deletePooja = async (poojaDocId) => {
  try {
    await deleteDoc(doc(db, COLLECTION, poojaDocId));
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// ─── Upload image to Firebase Storage ─────────────────────────────────────
export const uploadPoojaImage = async (file) => {
  const storageRef = ref(storage, `poojas/${Date.now()}_${file.name}`);
  await uploadBytes(storageRef, file);
  return await getDownloadURL(storageRef);
};

// ─── Get pooja count ──────────────────────────────────────────────────────
export const getPoojaCount = async () => {
  try {
    const snap = await getCountFromServer(collection(db, COLLECTION));
    return { success: true, count: snap.data().count };
  } catch (error) {
    return { success: false, count: 0 };
  }
};